import { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { ArrowLeft, CreditCard, Star, MessageCircle, Loader2, RefreshCw, Link2, HardDrive, Radio, X, Lock, TrendingUp, ChevronDown, DollarSign } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import GoogleDriveLogo from "@/components/GoogleDriveLogo";
import axios from "axios";
import { motion } from "framer-motion";
import { TopLikedServicesFan } from "@/components/TopLikedServicesFan";
import { toast } from "sonner";
import { redirectToCheckout } from "@/lib/redirectToCheckout";
import { PayPalScriptProvider, PayPalButtons } from "@paypal/react-paypal-js";
import { UserAvatar } from "@/components/UserAvatar";
import { useAuth } from "@/contexts/AuthContext";
import { FounderBadge } from "@/components/FounderBadge";
import { LikeButton } from "@/components/LikeButton";
import { ServiceLikeButton } from "@/components/ServiceLikeButton";
import { MuteCreatorButton } from "@/components/MuteCreatorButton";
import CreatorPresenceBadge from "@/components/CreatorPresenceBadge";
import ExtraContentViewer from "@/components/ExtraContentViewer";
import { AmateurTvIcon, ChaturbateIcon, Cam4Icon, OlecamsIcon, StripchatIcon } from "@/components/CamPlatformIcons";
import MessageInputWithEmojis from "@/components/MessageInputWithEmojis";
import FollowCreatorButton from "@/components/FollowCreatorButton";
import LoginRequiredModal from "@/components/LoginRequiredModal";
import { API } from "../lib/api";
import FundraisingCard from "../components/fundraising/FundraisingCard";
import { useTranslation } from "react-i18next";
// ─── Social Icons ───
const IgIcon = () => (
  <svg viewBox="0 0 24 24" className="w-4 h-4" fill="none">
    <rect x="2.5" y="2.5" width="19" height="19" rx="5.5" stroke="white" strokeWidth="1.5"/>
    <circle cx="12" cy="12" r="4.5" stroke="white" strokeWidth="1.5"/>
    <circle cx="17.3" cy="6.7" r="1.2" fill="white"/>
  </svg>
);
const YtIcon = () => (
  <svg viewBox="0 0 24 24" className="w-4 h-4" fill="white">
    <path d="M21.8 8s-.2-1.4-.8-2c-.8-.8-1.6-.8-2-.9C16.6 5 12 5 12 5s-4.6 0-7 .1c-.4.1-1.2.1-2 .9-.6.6-.8 2-.8 2S2 9.6 2 11.2v1.5c0 1.6.2 3.2.2 3.2s.2 1.4.8 2c.8.8 1.8.8 2.3.9C6.8 19 12 19 12 19s4.6 0 7-.1c.4-.1 1.2-.1 2-.9.6-.6.8-2 .8-2s.2-1.6.2-3.2v-1.5C22 9.6 21.8 8 21.8 8zM10 15V9l5.5 3-5.5 3z"/>
  </svg>
);
const TtIcon = () => (
  <svg viewBox="0 0 24 24" className="w-4 h-4" fill="white">
    <path d="M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-2.88 2.5 2.89 2.89 0 01-2.89-2.89 2.89 2.89 0 012.89-2.89c.28 0 .54.04.79.1V9.01a6.27 6.27 0 00-.79-.05 6.34 6.34 0 00-6.34 6.34 6.34 6.34 0 006.34 6.34 6.34 6.34 0 006.33-6.34V8.87a8.18 8.18 0 004.78 1.52V7c-1.06 0-2.08-.31-2.96-.86l-.06-.45z"/>
  </svg>
);
const XIcon = () => (
  <svg viewBox="0 0 24 24" className="w-4 h-4" fill="white">
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-4.714-6.231-5.401 6.231H2.744l7.73-8.835L2.25 2.25H8.08l4.253 5.622 5.911-5.622zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
  </svg>
);
const FbIcon = () => (
  <svg viewBox="0 0 24 24" className="w-4 h-4" fill="white">
    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
  </svg>
);
const OfIcon = () => (
  <svg viewBox="0 0 24 24" className="w-4 h-4" fill="none">
    <circle cx="12" cy="12" r="9.5" fill="rgba(255,255,255,0.18)"/>
    <text x="12" y="16" textAnchor="middle" fill="white" fontSize="9.5" fontWeight="900" fontFamily="'Arial Black', Arial, sans-serif">OF</text>
  </svg>
);
const OsIcon = () => (
  <svg viewBox="0 0 24 24" className="w-4 h-4" fill="none">
    <path d="M12 2.5l1.9 3.1 3.6-.65-.8 3.6L19.2 11l-2.5 2.45.8 3.6-3.6-.65L12 19.5l-1.9-3.1-3.6.65.8-3.6L4.8 11l2.5-2.45-.8-3.6 3.6.65L12 2.5z" fill="white" fillOpacity="0.95"/>
    <path d="M9 11.5l2 2 4-4" stroke="#6a4cff" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);
const GlobeIcon = () => (
  <svg viewBox="0 0 24 24" className="w-4 h-4" fill="none" stroke="white" strokeWidth="1.5">
    <circle cx="12" cy="12" r="9"/>
    <ellipse cx="12" cy="12" rx="4" ry="9"/>
    <line x1="3" y1="9" x2="21" y2="9"/>
    <line x1="3" y1="15" x2="21" y2="15"/>
  </svg>
);
const TgIcon = () => (
  <svg viewBox="0 0 24 24" className="w-4 h-4" fill="white">
    <path d="M9.78 18.65l.28-4.23 7.68-6.92c.34-.31-.07-.46-.52-.19L7.74 13.3 3.64 12c-.88-.25-.89-.86.2-1.3l15.97-6.16c.73-.33 1.43.18 1.15 1.3l-2.72 12.81c-.19.91-.74 1.13-1.5.71L12.6 16.3l-1.99 1.93c-.23.23-.42.42-.83.42z"/>
  </svg>
);
const GdIcon = () => (
  <GoogleDriveLogo className="w-4 h-4" />
);

const SOCIALS = [
  { key: "instagram_url",    label: "Instagram",     bg: "bg-gradient-to-br from-amber-400 via-rose-500 to-purple-600", Icon: IgIcon },
  { key: "youtube_url",      label: "YouTube",       bg: "bg-red-600",      Icon: YtIcon },
  { key: "tiktok_url",       label: "TikTok",        bg: "bg-black border border-zinc-700", Icon: TtIcon },
  { key: "twitter_url",      label: "Twitter/X",     bg: "bg-black border border-zinc-700", Icon: XIcon },
  { key: "facebook_url",     label: "Facebook",      bg: "bg-[#1877F2]",    Icon: FbIcon },
  { key: "onlyfans_url",     label: "OnlyFans",      bg: "bg-[#00AFF0]",    Icon: OfIcon },
  { key: "onlysafe_url",     label: "OnlySafe",      bg: "bg-[#6a4cff]",    Icon: OsIcon },
  { key: "website_url",      label: "Web",           bg: "bg-zinc-600",     Icon: GlobeIcon },
  { key: "telegram_url",     label: "Telegram",      bg: "bg-[#229ED9]",    Icon: TgIcon },
  { key: "google_drive_url", label: "Google Drive",  bg: "bg-white",        Icon: GdIcon },
];

const CAM_SOCIALS = [
  { key: "amateur_tv_url",  label: "amateur.tv",  color: "#222222",  Icon: AmateurTvIcon },
  { key: "chaturbate_url",  label: "Chaturbate",  color: "#FA7D1B",  Icon: ChaturbateIcon },
  { key: "cam4_url",        label: "CAM4",        color: "#1A1A1A",  Icon: Cam4Icon },
  { key: "olecams_url",     label: "Olecams",     color: "#F57D22",  Icon: OlecamsIcon },
  { key: "stripchat_url",   label: "Stripchat",   color: "#B52035",  Icon: StripchatIcon },
];

export default function TipPage() {
  const { modelId } = useParams();
  const navigate = useNavigate();
  const { user: authUser } = useAuth();
  const { t } = useTranslation();
  const [model, setModel] = useState(null);
  const [packages, setPackages] = useState([]);
  const [services, setServices] = useState([]);
  // Canonical creator user_id (URL may carry a slug). Used as the localStorage
  // key for the per-category "seen" cache below.
  const [realModelId, setRealModelId] = useState(null);
  // Active category pill in the fan-facing services list. `null` means
  // "show all categories at once" — used as the initial state until the
  // fan picks a pill.
  const [activeCategory, setActiveCategory] = useState(null);
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [selectingId, setSelectingId]         = useState(null); // para animación tipPulse
  const [selectedService, setSelectedService] = useState(null);
  const [customAmount, setCustomAmount] = useState("");
  const [isCustom, setIsCustom] = useState(false);
  const [loading, setLoading] = useState(false);
  const [subscribing, setSubscribing] = useState(null); // service_id being subscribed
  const [subPreviews, setSubPreviews] = useState({});    // { [service_id]: preview data }
  const [paymentMethod, setPaymentMethod] = useState("stripe");
  const [message, setMessage] = useState("");
  const [tipMin, setTipMin] = useState(5);
  const [paypalClientId, setPaypalClientId] = useState(process.env.REACT_APP_PAYPAL_CLIENT_ID || "");
  const [paymentMode, setPaymentMode] = useState("live");
  // Effective commission for the logged-in consumer (defaults to 15% until fetched)
  const [feeInfo, setFeeInfo] = useState({ rate: 0.15, pct: 15, is_custom: false, expires_at: null, label: "Comisión estándar de la plataforma" });
  // Premium content: does this creator have active premium services?
  const [hasPremiumServices, setHasPremiumServices] = useState(false);
  const [premiumServices, setPremiumServices] = useState([]);
  // Tracks if there's new premium content the fan hasn't seen yet
  const [hasNewPremiumContent, setHasNewPremiumContent] = useState(false);
  // Canal: canales del creador
  const [creatorChannels, setCreatorChannels] = useState([]);
  const [channelUnread, setChannelUnread] = useState(0);
  const [showChannelPicker, setShowChannelPicker] = useState(false);
  const [showPremiumPicker, setShowPremiumPicker] = useState(false);
  const [showFundraising, setShowFundraising] = useState(false);

  // Resetear la sección de recaudación al cambiar de modelo (evita que quede abierta al volver)
  useEffect(() => {
    setShowFundraising(false);
    setFrPage(1);
  }, [modelId]);
  const [hasNewFundraising, setHasNewFundraising] = useState(false);
  // Login modal: shown when an unauthenticated user tries to start any payment.
  const [needLogin, setNeedLogin] = useState(false);
  // Fundraising posts for this creator
  const [fundraisingPosts, setFundraisingPosts] = useState([]);
  const [frPage, setFrPage] = useState(1);
  const FR_PAGE_SIZE = 4;

  const MAX_MSG = 90;

  // Guard helper used by every payment entry point (tip, service, subscription,
  // PayPal). Returns true if the user is authenticated; if not, opens the
  // login modal and returns false so the caller can early-return.
  const requireAuth = () => {
    if (!authUser) {
      setNeedLogin(true);
      return false;
    }
    return true;
  };

  // Fetch fundraising posts for this creator (parallel, non-blocking)
  useEffect(() => {
    if (!modelId) return;
    axios.get(`${API}/fundraising/posts/by-creator/${modelId}`)
      .then((r) => {
        const posts = r.data || [];
        setFundraisingPosts(posts);
        if (posts.length > 0) {
          // Calcular si hay cambios desde la última visita
          const latestAt = Math.max(...posts.map(p => Math.max(
            new Date(p.created_at || 0).getTime(),
            new Date(p.updated_at  || 0).getTime(),
          )));
          const seenKey = `startip_fr_seen_${modelId}`;
          const seenRaw = localStorage.getItem(seenKey);
          let seenAt = 0;
          if (seenRaw) {
            try { seenAt = new Date(JSON.parse(seenRaw).ts || seenRaw).getTime(); }
            catch { seenAt = new Date(seenRaw).getTime(); } // compat formato antiguo
          }
          if (!seenRaw || latestAt > seenAt) setHasNewFundraising(true);
          // Siempre escribir al visitar perfil: actualiza ts Y count para que homepage
          // calcule correctamente los posts nuevos (diff = total - lastSeenCount).
          try {
            localStorage.setItem(seenKey, JSON.stringify({
              ts: new Date().toISOString(),
              count: posts.length,
            }));
          } catch (_) {}
        }
      })
      .catch(() => {});
  }, [modelId]);

  // Fetch personalised commission for logged-in consumer
  useEffect(() => {
    axios.get(`${API}/me/commission`, { withCredentials: true })
      .then(res => setFeeInfo(res.data))
      .catch(() => {}); // silent: keep default 15%
  }, []);

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/models/${modelId}`, { withCredentials: true }),
      axios.get(`${API}/tip-packages`),
      axios.get(`${API}/models/${modelId}/services`),
      axios.get(`${API}/platform-config`),
    ])
      .then(([modelRes, pkgRes, svcRes, cfgRes]) => {
        setModel(modelRes.data);
        setPackages(pkgRes.data);
        setServices(svcRes.data);
        // Persist canonical id for the category-pulse logic + seen cache.
        if (modelRes.data?.user_id) setRealModelId(modelRes.data.user_id);
        setTipMin(cfgRes.data.tip_min ?? 5);
        if (cfgRes.data.paypal_client_id) setPaypalClientId(cfgRes.data.paypal_client_id);
        if (cfgRes.data.payment_mode)     setPaymentMode(cfgRes.data.payment_mode);
        // Resolve the real internal user_id (modelId from URL might be a slug).
        const realUserId = modelRes.data?.user_id || modelId;
        // Check premium services — almacena la lista completa con conteo por servicio
        axios.get(`${API}/premium/model/${modelId}/services`, { withCredentials: true })
          .then(r => {
            const svcs = r.data?.services || [];
            setPremiumServices(svcs);
            setHasPremiumServices(svcs.length > 0);
          })
          .catch(() => {});
        // Badge "NUEVO" de contenido premium:
        // Para fans LOGUEADOS: el servidor siempre devuelve has_new_premium_content (MongoDB cross-device).
        //   Usa premium_content_seen.last_seen_at comparado con carruseles publicados por suscripción activa.
        // Para ANÓNIMOS: fallback a localStorage (premium_content_updated_at vs startip_premium_seen_*).
        if ("has_new_premium_content" in modelRes.data) {
          // Servidor calculó el badge → usuario autenticado, cross-device via MongoDB
          setHasNewPremiumContent(Boolean(modelRes.data.has_new_premium_content));
        } else {
          // Anónimo → localStorage fallback
          const premiumUpdatedAt = modelRes.data?.premium_content_updated_at;
          const premiumNewCount = modelRes.data?.premium_new_posts_count || 0;
          const realUserId = modelRes.data?.user_id || modelId;
          if (premiumUpdatedAt) {
            const seenKey = `startip_premium_seen_${realUserId}`;
            let seenAt = null;
            try {
              const raw = localStorage.getItem(seenKey);
              if (raw) {
                try { seenAt = JSON.parse(raw)?.at || null; } catch { seenAt = raw; }
              }
            } catch {}
            if (!seenAt || new Date(premiumUpdatedAt) > new Date(seenAt)) {
              setHasNewPremiumContent(true);
            }
            try {
              localStorage.setItem(seenKey, JSON.stringify({ at: new Date().toISOString(), count: premiumNewCount }));
            } catch {}
          }
        }
        // Check if creator has channels
        axios.get(`${API}/channels/creator/${realUserId}`, { withCredentials: true })
          .then(r => {
            const channels = r.data?.channels || [];
            if (channels.length) {
              setCreatorChannels(channels);
              // Fetch unread totals across ALL channels this fan follows for this creator.
              // Uses creator-unreads (ya existente en HomePage) en vez de solo channels[0],
              // para que el badge sea correcto cuando la creadora tiene varios canales.
              if (authUser) {
                axios.get(`${API}/channels/creator-unreads`, { withCredentials: true })
                  .then(u => {
                    const unreadsMap = u.data?.unreads || {};
                    setChannelUnread(unreadsMap[realUserId] || 0);
                  })
                  .catch(() => {});
              }
            }
          })
          .catch(() => {});
        // Record profile visit (fire-and-forget — never blocks the UI).
        // Always use the resolved user_id so the backend $inc matches the document
        // (legacy versions sent the slug, causing silent no-ops on slug URLs).
        axios.post(`${API}/models/${realUserId}/visit`, {}, { withCredentials: true }).catch(() => {});
        // Mark profile as "seen" so the luminescent update glow clears for this user.
        // For logged-in users: persisted in DB (cross-device).
        // For all users: also stored in localStorage for anonymous glow detection.
        // We call the endpoint with the slug/id from URL (backend resolves it),
        // and we ALSO seed localStorage with the REAL user_id so HomePage's
        // anonymous comparison against `model.user_id` matches correctly.
        axios.post(`${API}/models/${modelId}/mark-seen`, {}, { withCredentials: true }).catch(() => {});
        try {
          const nowIso = new Date().toISOString();
          localStorage.setItem(`startip_seen_${realUserId}`, nowIso);
          // Backwards-compat: also seed under the URL key (slug) so older code paths
          // using the slug as cache key remain consistent.
          if (modelId !== realUserId) localStorage.setItem(`startip_seen_${modelId}`, nowIso);
        } catch { /* presence cache write is best-effort */ }
      })
      .catch(() => {
        toast.error(t("tipPage.errorLoad"));
        navigate("/");
      });
  }, [modelId, navigate]);

  const handleSelectPackage = (pkg) => {
    setSelectedPackage(pkg.id);
    setSelectedService(null);
    setIsCustom(false);
    setCustomAmount("");
  };

  const [requestingChat, setRequestingChat] = useState(false);
  const [showChatConfirm, setShowChatConfirm] = useState(false);
  // Existing private chat request with this creator (if any)
  const [existingChatRequest, setExistingChatRequest] = useState(null); // null | {id, status}

  // Load existing chat request status when user is logged in
  useEffect(() => {
    if (!authUser || !modelId) return;
    // Compare against the canonical user_id (model.user_id), not the slug.
    // Otherwise the indicator never finds an existing request on slug URLs.
    const targetId = model?.user_id || modelId;
    axios.get(`${API}/chats/private/mine`, { withCredentials: true })
      .then((res) => {
        const sent = res.data?.as_requester || [];
        const found = sent.find((c) => c.creator_id === targetId);
        setExistingChatRequest(found ? { id: found.id, status: found.status } : null);
      })
      .catch(() => { /* silent — not blocking */ });
  }, [authUser, modelId, model?.user_id]);

  const handleRequestPrivateChat = async () => {
    if (!authUser) { setNeedLogin(true); return; }
    // CRITICAL: send the canonical user_id, never the slug from the URL.
    // `modelId` from useParams() may be a slug (e.g. "kristel-amateur"); the
    // backend looks up by user_id and would 404. The model object resolved
    // from `/models/:slug` already exposes the real user_id.
    const targetId = model?.user_id || modelId;
    setRequestingChat(true);
    try {
      const res = await axios.post(
        `${API}/chats/private/request`,
        { creator_id: targetId },
        { withCredentials: true }
      );
      setExistingChatRequest({ id: res.data.id, status: res.data.status });
      navigate(`/private-chats/${res.data.id}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || t("tipPage.errorRequestChat"));
    } finally {
      setRequestingChat(false);
    }
  };

  const handleSelectService = (svc) => {
    if (selectedService === svc.id) {
      setSelectedService(null);
      return;
    }
    setSelectedService(svc.id);
    setSelectedPackage(null);
    setIsCustom(false);
    setCustomAmount("");
  };

  const handleSubscribe = async (svc) => {
    if (!requireAuth()) return;
    setSubscribing(svc.id);
    try {
      const res = await axios.post(
        `${API}/subscriptions/checkout`,
        { service_id: svc.id, origin_url: window.location.origin },
        { withCredentials: true }
      );
      redirectToCheckout(res.data.checkout_url);
    } catch (err) {
      const status = err.response?.status;
      if (status === 401) {
        setNeedLogin(true);
      } else {
        toast.error(err.response?.data?.detail || t("tipPage.errorSubscription"));
      }
      setSubscribing(null);
    }
  };

  // Fetch subscription pricing preview for all subscription services.
  // Refetched on window focus so that, when the fan comes back from a Stripe
  // checkout (whether they paid or abandoned), the `already_subscribed` flag
  // is always re-evaluated against the latest server state. This is the safety
  // net that prevents the "Ya estás suscrito" stale state.
  useEffect(() => {
    const subServices = services.filter(s => s.is_subscription);
    if (subServices.length === 0) return;
    let cancelled = false;
    const fetchPreviews = async () => {
      const results = {};
      await Promise.all(
        subServices.map(async (svc) => {
          try {
            const r = await axios.get(`${API}/subscriptions/preview/${svc.id}`, { withCredentials: true });
            results[svc.id] = r.data;
          } catch (err) {
            const detail = err.response?.data?.detail || "";
            results[svc.id] = { error: detail };
          }
        })
      );
      if (!cancelled) setSubPreviews(results);
    };
    fetchPreviews();
    const onFocus = () => fetchPreviews();
    window.addEventListener("focus", onFocus);
    return () => {
      cancelled = true;
      window.removeEventListener("focus", onFocus);
    };
  }, [services]);

  const handleCustom = () => {
    setSelectedPackage(null);
    setSelectedService(null);
    setIsCustom(true);
  };

  const hasSelection = selectedPackage || selectedService || isCustom;

  const getSelectedAmount = () => {
    if (selectedService) {
      const svc = services.find((s) => s.id === selectedService);
      return svc ? `${svc.price}€` : "";
    }
    if (isCustom) return customAmount ? `${customAmount}€` : "...";
    const pkg = packages.find((p) => p.id === selectedPackage);
    return pkg ? `${pkg.amount}€` : "";
  };

  const getTotalToPay = () => {
    let baseAmt = 0;
    if (selectedService) {
      const svc = services.find((s) => s.id === selectedService);
      baseAmt = svc ? svc.price : 0;
    } else if (selectedPackage) {
      const pkg = packages.find((p) => p.id === selectedPackage);
      baseAmt = pkg ? pkg.amount : 0;
    } else if (isCustom && customAmount && !isNaN(parseFloat(customAmount))) {
      baseAmt = parseFloat(customAmount);
    }
    if (baseAmt <= 0) return null;
    return parseFloat((baseAmt * (1 + feeInfo.rate)).toFixed(2));
  };

  const buildTipBody = () => {
    const body = { model_id: modelId };
    if (selectedService) body.service_id = selectedService;
    else if (isCustom) body.custom_amount = parseFloat(customAmount);
    else body.package_id = selectedPackage;
    if (message.trim()) body.message = message.trim();
    // Attach buyer_id from logged-in session (enables service chat creation)
    if (authUser?.user_id) body.buyer_id = authUser.user_id;
    return body;
  };

  const handlePay = async () => {
    if (!hasSelection) { toast.error(t("tipPage.selectAmount")); return; }
    if (isCustom && (isNaN(parseFloat(customAmount)) || parseFloat(customAmount) < tipMin)) {
      toast.error(`La propina mínima actual es de ${tipMin} €`); return;
    }
    if (!requireAuth()) return;
    setLoading(true);
    try {
      const body = { ...buildTipBody(), origin_url: window.location.origin };
      const res = await axios.post(`${API}/tips/checkout`, body, { withCredentials: true });
      redirectToCheckout(res.data.url);
    } catch (err) {
      const status = err.response?.status;
      if (status === 401) {
        setNeedLogin(true);
      } else {
        toast.error(err.response?.data?.detail || "Error");
      }
      setLoading(false);
    }
  };

  if (!model) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-pulse text-neon-pink text-xl font-heading font-bold">CARGANDO...</div>
      </div>
    );
  }

  const photoSrc = model.photo_url || null;

  // ── Avatar fallback helpers (same as HomePage) ──────────────────────────────
  const _tipAvatarGradient = (g) => {
    if (g === "mujer")  return "linear-gradient(160deg, #be123c 0%, #9333ea 100%)";
    if (g === "hombre") return "linear-gradient(160deg, #0369a1 0%, #0f766e 100%)";
    return "linear-gradient(160deg, #6d28d9 0%, #2563eb 100%)";
  };

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b border-border px-4 py-4" data-safe-top="true">
        <div className="max-w-md mx-auto flex items-center gap-3">
          <Link to="/" className="text-zinc-400 hover:text-white transition-colors" data-testid="back-link">
            <ArrowLeft className="w-5 h-5" />
          </Link>
          <h1 className="font-heading font-bold text-white text-sm uppercase tracking-wide">
            STAR<span className="text-neon-pink">TIP</span>
          </h1>
        </div>
      </header>

      <div className="max-w-md mx-auto px-4 py-6 space-y-6">
        {/* Model Info */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="flex items-center gap-4">
          <div className="relative shrink-0">
            <div className="w-20 h-20 rounded-xl border-2 border-border overflow-hidden">
              {photoSrc ? (
                <UserAvatar
                  src={photoSrc}
                  alt={model.name}
                  privacyMode={model.avatar_privacy_mode || "normal"}
                  className="w-full h-full object-cover"
                  data-testid="model-photo"
                />
              ) : (
                <div
                  className="w-full h-full flex items-end justify-center"
                  style={{ background: _tipAvatarGradient(model.gender) }}
                  data-testid="model-photo"
                >
                  <svg viewBox="0 0 100 120" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" style={{ width: "65%", height: "auto", opacity: 0.55 }}>
                    <circle cx="50" cy="36" r="22" fill="white" />
                    <path d="M5 120 Q5 72 50 72 Q95 72 95 120 Z" fill="white" />
                  </svg>
                </div>
              )}
            </div>
            {model.is_founder && (
              <div className="absolute -top-1.5 -left-1.5 z-10">
                <FounderBadge size="sm" />
              </div>
            )}
            {/* EN DIRECTO indicator — shown when any cam platform is live */}
            {model.any_live_active && (
              <div
                className="absolute -bottom-2.5 left-1/2 -translate-x-1/2 z-20 flex items-center gap-1 px-2 py-0.5 rounded-full"
                style={{ background: "rgba(220,38,38,0.95)", backdropFilter: "blur(4px)" }}
                title={model?.gender === "hombre"
                  ? "El creador ha marcado que está en directo en una de sus plataformas de cams"
                  : "La creadora ha marcado que está en directo en una de sus plataformas de cams"}
                data-testid="tippage-live-badge"
              >
                <span className="relative flex h-1.5 w-1.5">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-white opacity-80" />
                  <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-white" />
                </span>
                <span className="text-white text-[8px] font-heading font-bold uppercase tracking-widest whitespace-nowrap">EN DIRECTO</span>
              </div>
            )}
            {/* Canal activo — badge eliminado del perfil interior (se muestra en tarjetas de portada) */}
          </div>
          <div className="min-w-0">
    <h2 className={`font-heading font-bold text-white whitespace-nowrap ${(() => {
      const scale = ["text-xs","text-sm","text-base","text-lg","text-xl","text-2xl","text-3xl"];
      const len = (model.name || "").length;
      let idx = 5;
      if (len >= 24) idx -= 3; else if (len >= 18) idx -= 2; else if (len >= 12) idx -= 1;
      return scale[idx] + (len >= 10 ? " tracking-tight" : "");
    })()} leading-tight`} data-testid="model-name">{model.name}</h2>
            {/* Badge de cuenta oficial de StarTip Staff */}
            {model.platform_staff && (
              <div className="mt-1.5">
                <span
                  className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-heading font-black uppercase tracking-wide bg-amber-500/20 border border-amber-500/40 text-amber-400"
                  data-testid="model-staff-badge"
                  title="Cuenta oficial de StarTip"
                >
                  <Star className="w-3 h-3 fill-current" />StarTip Official
                </span>
              </div>
            )}
            <p className="text-sm text-zinc-400 font-body">{model.description}</p>
            {/* Like button — visible solo cuando no eres la propia creadora.
                OJO: comparamos y pasamos siempre el user_id canónico (no el slug de la URL).
                Cuando la URL usa slug (p. ej. /tip/diana) `modelId` es el slug,
                así que usamos `model?.user_id` que ya viene resuelto desde el backend. */}
            {authUser?.user_id !== model?.user_id && (
              <div className="mt-1.5 flex items-center gap-2 flex-wrap">
                <LikeButton
                  creatorId={model?.user_id || modelId}
                  initialCount={model.likes_count || 0}
                  size="sm"
                  showCount={true}
                />
                <MuteCreatorButton
                  creatorId={model?.user_id || modelId}
                  isSelf={authUser?.user_id === model?.user_id}
                  userRole={authUser?.role}
                />
                {/* Solo otra creadora puede seguir; el componente decide si mostrarse */}
                <FollowCreatorButton creatorId={model?.user_id || modelId} testId="tip-follow-creator-btn" />
              </div>
            )}
            {/* Última conexión + estado de notificaciones de chats — sutil,
                solo visible para usuarios autenticados. La creadora dueña
                del perfil ve la suya; el componente gestiona la visibilidad
                interna según el rol. Usa user_id canónico (no slug). */}
            <CreatorPresenceBadge
              creatorIdOrSlug={model?.user_id || modelId}
              ownerUserId={model?.user_id}
            />
            {/* Botón "VER MÁS" — extras de la modelo (fotos + vídeo). Solo
                aparece si tiene contenido y el visitante está autenticado.
                El propio componente esconde si no hay nada que mostrar. */}
            <ExtraContentViewer creatorIdOrSlug={model?.user_id || modelId} gender={model?.gender} />
            {/* Social Links */}
            <div className="flex gap-2 mt-2 flex-wrap" data-testid="social-links">
              {SOCIALS.filter(({ key }) => {
                if (!model[key]) return false;
                const socialKey = key.replace("_url", "");
                return !(model.disabled_socials || []).includes(socialKey);
              }).map(({ key, label, bg, Icon }) => (
                <a
                  key={key}
                  href={model[key]}
                  target="_blank"
                  rel="noopener noreferrer"
                  title={label}
                  className={`inline-flex items-center justify-center w-9 h-9 rounded-xl ${bg} hover:opacity-80 hover:scale-110 transition-all duration-200 shrink-0`}
                  data-testid={`social-${key.replace("_url", "")}`}
                >
                  <Icon />
                </a>
              ))}
            </div>
            {/* Webcam Platform Links */}
            {CAM_SOCIALS.some(({ key }) => model[key]) && (
              <div className="mt-3" data-testid="cam-platform-links">
                <p className="text-[9px] text-orange-400/80 font-body uppercase tracking-widest mb-1.5">Webcam en directo</p>
                <div className="flex gap-2 flex-wrap">
                  {CAM_SOCIALS.filter(({ key }) => model[key]).map(({ key, label, color, Icon }) => (
                    <a
                      key={key}
                      href={model[key]}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid={`cam-${key.replace("_url", "")}`}
                      className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-white text-[11px] font-body font-semibold hover:opacity-80 hover:scale-105 transition-all duration-200"
                      style={{ background: color }}
                    >
                      <Icon />
                      {label}
                    </a>
                  ))}
                </div>
              </div>
            )}
          </div>
        </motion.div>

        {/* ── Private chat request button ── */}
        {authUser && authUser.user_id !== (model?.user_id || modelId) && (
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.04 }}>
            {existingChatRequest?.status === "ACCEPTED" ? (
              <button
                onClick={() => navigate(`/private-chats/${existingChatRequest.id}`)}
                className="w-full flex items-center justify-center gap-2 py-3 rounded-xl border-2 border-neon-green/40 bg-neon-green/10 hover:bg-neon-green/20 text-neon-green text-sm font-heading font-bold uppercase tracking-wide transition-all duration-200"
                data-testid="request-private-chat-btn"
              >
                <MessageCircle className="w-4 h-4" /> Ir al chat privado
              </button>
            ) : existingChatRequest?.status === "PENDING" ? (
              <button
                onClick={() => navigate(`/private-chats/${existingChatRequest.id}`)}
                className="w-full flex items-center justify-center gap-2 py-3 rounded-xl border-2 border-yellow-500/30 bg-yellow-500/5 hover:bg-yellow-500/10 hover:border-yellow-500/60 text-yellow-400 text-sm font-heading font-bold uppercase tracking-wide transition-all duration-200"
                data-testid="request-private-chat-btn"
              >
                <MessageCircle className="w-4 h-4" /> Ver solicitud pendiente
              </button>
            ) : (
              <button
                onClick={() => setShowChatConfirm(true)}
                disabled={requestingChat}
                className="w-full flex items-center justify-center gap-2 py-3 rounded-xl border-2 border-neon-cyan/30 bg-neon-cyan/5 hover:bg-neon-cyan/10 hover:border-neon-cyan/60 text-neon-cyan text-sm font-heading font-bold uppercase tracking-wide transition-all duration-200 disabled:opacity-50"
                data-testid="request-private-chat-btn"
              >
                {requestingChat
                  ? <><RefreshCw className="w-4 h-4 animate-spin" /> Enviando…</>
                  : <><MessageCircle className="w-4 h-4" /> Solicitar chat privado</>}
              </button>
            )}
          </motion.div>
        )}
        {!authUser && (
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.04 }}>
            <button
              onClick={() => setNeedLogin(true)}
              className="w-full flex items-center justify-center gap-2 py-3 rounded-xl border-2 border-zinc-700 bg-zinc-900/50 hover:border-zinc-500 text-zinc-400 hover:text-white text-sm font-heading font-bold uppercase tracking-wide transition-all duration-200"
              data-testid="anon-chat-login-btn"
            >
              <MessageCircle className="w-4 h-4" /> Inicia sesión para chatear
            </button>
          </motion.div>
        )}
        {/* ── Contenido Premium: botón único con picker ──────────────────── */}
        {hasPremiumServices && authUser && authUser.user_id !== modelId && (() => {
          const totalNew = premiumServices.filter(s => s.has_access).reduce((acc, s) => acc + (s.new_content_count || 0), 0);
          const hasNew   = totalNew > 0;
          const btnCls   = `w-full flex items-center gap-3 px-4 py-3 rounded-xl border-2 text-sm font-heading font-bold uppercase tracking-wide transition-all duration-200 relative overflow-hidden ${
            hasNew
              ? "border-purple-400/80 bg-purple-900/20 text-purple-200 shadow-[0_0_18px_2px_rgba(168,85,247,0.35)]"
              : "border-purple-500/40 bg-purple-900/10 hover:bg-purple-900/20 hover:border-purple-500/70 text-purple-300"
          }`;
          const iconCls = `w-4 h-4 flex-shrink-0 ${hasNew ? "animate-bounce" : ""}`;
          const badge = hasNew ? (
            <span className="flex-shrink-0 px-1.5 py-0.5 rounded-full bg-purple-500 text-white text-[9px] font-black min-w-[20px] text-center">
              {totalNew}
            </span>
          ) : (
            <ChevronDown className="w-4 h-4 flex-shrink-0 text-purple-400/50" />
          );

          if (premiumServices.length === 1) {
            const svc = premiumServices[0];
            // Un solo servicio: navegar directamente
            return (
              <motion.div key="premium-single" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.045 }}>
                {svc.has_access ? (
                  <Link to={`/premium/content/${svc.id}`} className={btnCls} data-testid="premium-content-btn">
                    {hasNew && <span className="absolute inset-0 animate-pulse bg-purple-500/8 pointer-events-none" />}
                    <Star className={iconCls} />
                    <span className="flex-1 truncate">{svc.name}</span>
                    {badge}
                  </Link>
                ) : (
                  <Link to={`/premium/${model?.slug || modelId}`} className={btnCls} data-testid="premium-content-btn">
                    <Lock className="w-4 h-4 flex-shrink-0" />
                    <span className="flex-1 truncate">{svc.name}</span>
                    <span className="flex-shrink-0 text-purple-400/60 text-[10px] font-normal normal-case tracking-normal">
                      {svc.price ? `${Number(svc.price).toFixed(2)} €` : "Gratis"}
                    </span>
                  </Link>
                )}
              </motion.div>
            );
          }

          // Múltiples servicios: abre picker
          return (
            <motion.div key="premium-multi" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.045 }}>
              <button type="button" onClick={() => setShowPremiumPicker(true)} className={btnCls} data-testid="premium-content-btn">
                {hasNew && <span className="absolute inset-0 animate-pulse bg-purple-500/8 pointer-events-none" />}
                <Star className={iconCls} />
                <span className="flex-1">Contenido Premium</span>
                {badge}
              </button>
            </motion.div>
          );
        })()}

        {/* Visitante no logueado: ve el botón con candado */}
        {hasPremiumServices && !authUser && (
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.045 }}>
            <button
              type="button"
              onClick={() => setNeedLogin(true)}
              className="w-full flex flex-col items-center justify-center gap-2 py-4 rounded-xl border-2 border-purple-500/40 bg-purple-950/20 hover:bg-purple-950/40 hover:border-purple-400/60 transition-all duration-200 group relative overflow-hidden"
              data-testid="premium-content-login-btn"
            >
              <span className="absolute inset-0 bg-gradient-to-b from-purple-600/5 to-transparent pointer-events-none" />
              <Lock className="w-6 h-6 text-purple-400 group-hover:text-purple-300 transition-colors" />
              <span className="text-sm font-heading font-black uppercase tracking-widest text-purple-300 group-hover:text-purple-200 transition-colors leading-tight text-center">
                Contenido Premium
              </span>
              <span className="text-[11px] text-purple-400/70 group-hover:text-purple-300/80 transition-colors font-medium">
                Inicia sesión para ver
              </span>
            </button>
          </motion.div>
        )}

        {/* ── Premium picker modal ─────────────────────────────────────── */}
        {showPremiumPicker && (
          <div className="fixed inset-0 z-50 bg-black/80 flex items-end sm:items-center justify-center px-4 pb-6 sm:pb-0" onClick={() => setShowPremiumPicker(false)}>
            <div className="bg-zinc-900 border border-white/10 rounded-2xl w-full max-w-sm overflow-hidden" onClick={e => e.stopPropagation()}>
              <div className="flex items-center justify-between px-4 py-3 border-b border-white/5">
                <div className="flex items-center gap-2">
                  <Star className="w-4 h-4 text-purple-400" />
                  <span className="text-white font-bold text-sm">Contenido Premium</span>
                </div>
                <button onClick={() => setShowPremiumPicker(false)} className="p-1 hover:bg-white/10 rounded-full transition">
                  <X className="w-4 h-4 text-zinc-400" />
                </button>
              </div>
              <div className="p-3 space-y-2">
                {premiumServices.map(svc => {
                  const isNew = svc.has_access && (svc.new_content_count || 0) > 0;
                  return svc.has_access ? (
                    <Link
                      key={svc.id}
                      to={`/premium/content/${svc.id}`}
                      onClick={() => setShowPremiumPicker(false)}
                      data-testid={`premium-picker-item-${svc.id}`}
                      className={`flex items-center gap-3 p-3 rounded-xl transition ${isNew ? "bg-purple-900/40 hover:bg-purple-900/60" : "bg-zinc-800 hover:bg-zinc-700"}`}
                    >
                      <div className="w-10 h-10 rounded-xl flex-shrink-0 bg-purple-900/40 border border-purple-500/30 flex items-center justify-center">
                        <Star className={`w-5 h-5 ${isNew ? "text-purple-300 animate-bounce" : "text-purple-500"}`} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-white font-semibold text-sm truncate">{svc.name}</p>
                        <p className="text-purple-400/70 text-xs">
                          {svc.price ? `${Number(svc.price).toFixed(2)} €` : "Gratis"}
                        </p>
                      </div>
                      {isNew && (
                        <span className="flex-shrink-0 px-1.5 py-0.5 rounded-full bg-purple-500 text-white text-[9px] font-black min-w-[20px] text-center">
                          {svc.new_content_count}
                        </span>
                      )}
                    </Link>
                  ) : (
                    <Link
                      key={svc.id}
                      to={`/premium/${model?.slug || modelId}`}
                      onClick={() => setShowPremiumPicker(false)}
                      data-testid={`premium-picker-item-${svc.id}`}
                      className="flex items-center gap-3 p-3 rounded-xl bg-zinc-800/60 hover:bg-zinc-800 transition opacity-70 hover:opacity-100"
                    >
                      <div className="w-10 h-10 rounded-xl flex-shrink-0 bg-zinc-700 flex items-center justify-center">
                        <Lock className="w-5 h-5 text-purple-500/50" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-zinc-300 font-semibold text-sm truncate">{svc.name}</p>
                        <p className="text-zinc-500 text-xs">
                          {svc.price ? `${Number(svc.price).toFixed(2)} €` : "Gratis"} · Suscríbete para acceder
                        </p>
                      </div>
                    </Link>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        {/* ── Canal button ─────────────────────────── */}
        {creatorChannels.length === 1 && (
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 }}>
            <Link
              to={`/canal/${creatorChannels[0].id}`}
              state={{ creatorSlug: modelId }}
              data-testid="creator-channel-btn"
              className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-xl border-2 font-heading font-bold uppercase tracking-wide transition-all duration-200 text-white text-sm relative"
              style={{ backgroundColor: "#2AABEE18", borderColor: "#2AABEE55", color: "#2AABEE" }}
              onMouseEnter={e => { e.currentTarget.style.backgroundColor = "#2AABEE30"; e.currentTarget.style.borderColor = "#2AABEE"; }}
              onMouseLeave={e => { e.currentTarget.style.backgroundColor = "#2AABEE18"; e.currentTarget.style.borderColor = "#2AABEE55"; }}
            >
              <Radio className="w-4 h-4" />
              Canal de {model?.name || (model?.gender === "hombre" ? "el modelo" : "la modelo")}
              {creatorChannels[0].channel_type === "paid" && (
                <span className="flex items-center gap-0.5 bg-amber-500/20 text-amber-400 text-[10px] font-bold px-2 py-0.5 rounded-full ml-1">
                  <DollarSign className="w-2.5 h-2.5" />
                  {Number(creatorChannels[0].entry_price || 0).toFixed(2)}€
                  {creatorChannels[0].billing_type === "monthly" ? "/mes" : ""}
                </span>
              )}
              {channelUnread > 0 && (
                <span
                  data-testid="channel-unread-badge"
                  className="ml-1 bg-neon-green text-black text-[11px] font-black rounded-full min-w-[20px] h-5 flex items-center justify-center px-1.5 leading-none"
                >
                  {channelUnread >= 99 ? "99+" : channelUnread}
                </span>
              )}
            </Link>
          </motion.div>
        )}
        {creatorChannels.length > 1 && (
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 }}>
            <button
              type="button"
              data-testid="creator-channel-btn"
              onClick={() => setShowChannelPicker(true)}
              className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-xl border-2 font-heading font-bold uppercase tracking-wide transition-all duration-200 text-sm relative"
              style={{ backgroundColor: "#2AABEE18", borderColor: "#2AABEE55", color: "#2AABEE" }}
              onMouseEnter={e => { e.currentTarget.style.backgroundColor = "#2AABEE30"; e.currentTarget.style.borderColor = "#2AABEE"; }}
              onMouseLeave={e => { e.currentTarget.style.backgroundColor = "#2AABEE18"; e.currentTarget.style.borderColor = "#2AABEE55"; }}
            >
              <Radio className="w-4 h-4" />
              Canales de {model?.name || (model?.gender === "hombre" ? "el modelo" : "la modelo")}
              <span className="ml-1 bg-[#2AABEE] text-white text-[10px] font-black rounded-full w-5 h-5 flex items-center justify-center">
                {creatorChannels.length}
              </span>
              {channelUnread > 0 && (
                <span
                  data-testid="channel-unread-badge"
                  className="ml-1 bg-neon-green text-black text-[11px] font-black rounded-full min-w-[20px] h-5 flex items-center justify-center px-1.5 leading-none"
                >
                  {channelUnread >= 99 ? "99+" : channelUnread}
                </span>
              )}
            </button>
          </motion.div>
        )}

        {/* ── Channel picker modal ─────────────────────────── */}
        {showChannelPicker && (
          <div className="fixed inset-0 z-50 bg-black/80 flex items-end sm:items-center justify-center px-4 pb-6 sm:pb-0" onClick={() => setShowChannelPicker(false)}>
            <div className="bg-zinc-900 border border-white/10 rounded-2xl w-full max-w-sm overflow-hidden" onClick={e => e.stopPropagation()}>
              <div className="flex items-center justify-between px-4 py-3 border-b border-white/5">
                <div className="flex items-center gap-2">
                  <Radio className="w-4 h-4 text-[#2AABEE]" />
                  <span className="text-white font-bold text-sm">Elegir canal</span>
                </div>
                <button onClick={() => setShowChannelPicker(false)} className="p-1 hover:bg-white/10 rounded-full transition">
                  <X className="w-4 h-4 text-zinc-400" />
                </button>
              </div>
              <div className="p-3 space-y-2">
                {creatorChannels.map(ch => (
                  <Link
                    key={ch.id}
                    to={`/canal/${ch.id}`}
                    state={{ creatorSlug: modelId }}
                    data-testid={`channel-picker-item-${ch.id}`}
                    onClick={() => setShowChannelPicker(false)}
                    className="flex items-center gap-3 p-3 rounded-xl bg-zinc-800 hover:bg-zinc-700 transition"
                  >
                    <div className="w-10 h-10 rounded-xl flex-shrink-0 overflow-hidden bg-zinc-700 flex items-center justify-center">
                      {ch.avatar_url
                        ? <img src={ch.avatar_url} alt="" className="w-full h-full object-cover" />
                        : <Radio className="w-5 h-5 text-zinc-500" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-white font-semibold text-sm truncate">{ch.name}</p>
                      {ch.channel_type === "paid" ? (
                        <span className="inline-flex items-center gap-0.5 bg-amber-500/20 text-amber-400 text-[10px] font-bold px-2 py-0.5 rounded-full mt-0.5">
                          <DollarSign className="w-2.5 h-2.5" />
                          {Number(ch.entry_price || 0).toFixed(2)}€{ch.billing_type === "monthly" ? "/mes" : ""}
                        </span>
                      ) : (
                        <p className="text-zinc-500 text-xs capitalize">{ch.channel_type === "private" ? t("tipPage.privateChannel") : t("tipPage.publicChannel")}</p>
                      )}
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* ── 4. Objetivo de la modelo (Recaudación) ──────────────────── */}
        {fundraisingPosts.length > 0 && (
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.055 }}>
            <button
              type="button"
              data-testid="fundraising-goal-btn"
              onClick={() => {
                setShowFundraising(v => !v);
                // Marcar como visto al primer clic — el localStorage ya se escribió al cargar el perfil,
                // pero actualizamos el estado local para quitar el glow inmediatamente.
                if (hasNewFundraising) {
                  setHasNewFundraising(false);
                  try {
                    localStorage.setItem(`startip_fr_seen_${modelId}`, JSON.stringify({
                      ts: new Date().toISOString(),
                      count: fundraisingPosts.length,
                    }));
                  } catch (_) {}
                }
              }}
              className={`w-full flex items-center justify-center gap-2 py-3 rounded-xl border-2 font-heading font-bold uppercase tracking-wide transition-all duration-200 text-sm relative overflow-hidden
                ${hasNewFundraising
                  ? "shadow-[0_0_18px_2px_rgba(255,184,0,0.45)]"
                  : ""
                }`}
              style={{
                backgroundColor: hasNewFundraising
                  ? "rgba(255,184,0,0.18)"
                  : showFundraising
                  ? "rgba(255,184,0,0.15)"
                  : "rgba(255,184,0,0.08)",
                borderColor: hasNewFundraising ? "#FFB800" : showFundraising ? "#FFB800" : "rgba(255,184,0,0.35)",
                color: "#FFB800",
              }}
            >
              {hasNewFundraising && (
                <span className="absolute inset-0 animate-pulse bg-yellow-400/8 pointer-events-none" />
              )}
              <TrendingUp className={`w-4 h-4 ${hasNewFundraising ? "animate-bounce" : ""}`} />
              Objetivo de {model?.name || (model?.gender === "hombre" ? "el modelo" : "la modelo")}
              {hasNewFundraising && (
                <span className="ml-1 px-1.5 py-0.5 rounded-full bg-[#FFB800] text-black text-[9px] font-black tracking-wide">NUEVO</span>
              )}
              <ChevronDown
                className="w-4 h-4 transition-transform duration-200"
                style={{ transform: showFundraising ? "rotate(180deg)" : "rotate(0deg)" }}
              />
            </button>

            {showFundraising && (
              <div className="mt-3 space-y-4">
                {fundraisingPosts.slice(0, frPage * FR_PAGE_SIZE).map((post) => (
                  <FundraisingCard
                    key={post.id}
                    post={post}
                    creatorName={model?.name || ""}
                    creatorSlug={model?.slug || modelId}
                    creatorAvatar={model?.photo_url || null}
                    authUser={authUser}
                    paymentMode={paymentMode}
                  />
                ))}
                {fundraisingPosts.length > frPage * FR_PAGE_SIZE && (
                  <button
                    onClick={() => setFrPage(p => p + 1)}
                    className="w-full py-2.5 rounded-xl border border-amber-500/30 bg-amber-500/5 hover:bg-amber-500/10 transition-colors text-amber-400 text-xs font-heading font-bold uppercase tracking-wide"
                    data-testid="fundraising-ver-mas-btn"
                  >
                    Ver más ({fundraisingPosts.length - frPage * FR_PAGE_SIZE} restantes)
                  </button>
                )}
              </div>
            )}
          </motion.div>
        )}

        {services.length > 0 && (() => {
          // ── Category pills (fan side) ──────────────────────────────────────
          // The model classifies each service into VÍDEOS / FOTOS /
          // SUSCRIPCIONES / PACKS / OTROS and chooses an explicit
          // `sort_order` per service.  Backend already returns services
          // sorted by sort_order asc; we just group them here so the fan
          // can pick a pill and see only that bucket.
          const CAT_LABELS = {
            VIDEOS:        "VÍDEOS",
            FOTOS:         "FOTOS",
            SUSCRIPCIONES: "SUSCRIPCIONES",
            PACKS:         "PACKS",
            OTROS:         "OTROS",
          };
          const CAT_ORDER = ["VIDEOS", "FOTOS", "SUSCRIPCIONES", "PACKS", "OTROS"];
          const grouped = services.reduce((acc, s) => {
            const k = CAT_ORDER.includes(s.category) ? s.category : "OTROS";
            (acc[k] = acc[k] || []).push(s);
            return acc;
          }, {});
          const availableCats = CAT_ORDER.filter(k => (grouped[k] || []).length > 0);
          // Until the fan intentionally taps a pill, show no services —
          // they have to opt-in by clicking a category. This keeps the
          // landing screen clean and avoids auto-revealing content.
          const currentCat = activeCategory && availableCats.includes(activeCategory)
            ? activeCategory
            : null;
          const filtered = currentCat ? (grouped[currentCat] || []) : [];

          // ── Fixed 3-row pill layout ─────────────────────────────────
          // Row 1: VIDEOS | FOTOS · Row 2: SUSCRIPCIONES | PACKS · Row 3: OTROS
          // The five categories are always rendered to keep the layout
          // predictable across creators. Pills for empty categories
          // appear muted and are not clickable.
          //
          // ── "Hay contenido nuevo" pulse ─────────────────────────────
          // Reuse the same semantics as the homepage card glow:
          //   latest_service_in_cat.created_at > localStorage seen_at  → unseen
          // Persisted in localStorage under  startip_seen_${realModelId}_cat_${CAT}
          // and refreshed when the fan opens that pill. Falls back to slug if
          // we couldn't resolve the real user_id yet.
          const seenKey = (cat) => `startip_seen_${realModelId || modelId}_cat_${cat}`;
          const latestPerCat = grouped; // already built above
          const tsOf = (s) => s.updated_at || s.created_at || "";
          const catLatestTs = (cat) =>
            (latestPerCat[cat] || []).reduce(
              (max, s) => (tsOf(s) > max ? tsOf(s) : max),
              "",
            );
          const hasUnseen = (cat) => {
            const latest = catLatestTs(cat);
            if (!latest) return false;
            let seen = "";
            try {
              seen = localStorage.getItem(seenKey(cat)) || "";
            } catch { /* localStorage disabled */ }
            return !seen || seen < latest;
          };

          const renderPill = (cat) => {
            const count = (grouped[cat] || []).length;
            const isEmpty = count === 0;
            const active = currentCat === cat;
            const unseen = !isEmpty && hasUnseen(cat);
            return (
              <button
                key={cat}
                type="button"
                role="tab"
                aria-selected={active}
                disabled={isEmpty}
                onClick={() => {
                  if (isEmpty) return;
                  setActiveCategory(cat);
                  // Mark this category as "seen now" so the gold pulse stops
                  // immediately — same fire-and-forget pattern as the
                  // profile-wide mark-seen on TipPage load.
                  try {
                    localStorage.setItem(seenKey(cat), new Date().toISOString());
                  } catch { /* best-effort */ }
                }}
                className={`w-full px-3 py-1.5 rounded-full border text-[11px] font-heading font-bold uppercase tracking-wide transition-all whitespace-nowrap cat-pill-gold ${
                  unseen ? "cat-pill-gold-pulse" : ""
                } ${
                  active
                    ? "bg-neon-cyan text-black shadow-[0_0_12px_rgba(34,211,238,0.35)]"
                    : isEmpty
                      ? "bg-transparent text-zinc-600 cursor-not-allowed opacity-50"
                      : "bg-transparent text-zinc-400 hover:text-white"
                }`}
                data-testid={`services-cat-pill-${cat}`}
                data-unseen={unseen ? "true" : "false"}
              >
                {CAT_LABELS[cat]}
                {count > 0 && (
                  <span className={`ml-1.5 inline-flex items-center justify-center min-w-[16px] h-4 px-1 rounded-full text-[9px] font-bold ${active ? "bg-black/20 text-black" : "bg-zinc-800 text-zinc-400"}`}>
                    {count}
                  </span>
                )}
              </button>
            );
          };

          return (
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 }}>
            <p className="font-body text-sm text-zinc-400 mb-3 uppercase tracking-wide flex items-center gap-1">
              <Star className="w-3 h-3" /> {t("tipPage.services")}
            </p>

            {/* Category pills — 3-row fixed grid, never scrolls horizontally */}
            <div
              className="grid grid-cols-2 gap-1.5 mb-3"
              data-testid="services-category-pills"
              role="tablist"
            >
              {renderPill("VIDEOS")}
              {renderPill("FOTOS")}
              {renderPill("SUSCRIPCIONES")}
              {renderPill("PACKS")}
              <div className="col-span-2">
                {renderPill("OTROS")}
              </div>
            </div>

            {/* Top 3 servicios más likeados — visible para fans */}
            <TopLikedServicesFan modelId={modelId} />

            {/* Soft hint while no pill is active */}
            {!currentCat && availableCats.length > 0 && (
              <p
                className="text-center text-[11px] text-zinc-500 font-body mb-3"
                data-testid="services-hint-empty"
              >
                Pulsa una categoría para ver los servicios de{" "}
                <span className="text-zinc-300 font-semibold">
                  {(model?.name) || "esta modelo"}
                </span>
              </p>
            )}

            <div className="space-y-2">
              {filtered.map((svc) => (
                svc.is_subscription ? (
                  /* ── Quarterly subscription service card ── */
                  <div
                    key={svc.id}
                    className="rounded-xl border-2 border-neon-cyan/40 bg-card overflow-hidden"
                    data-testid={`service-btn-${svc.id}`}
                  >
                    {svc.teaser_image_url && (
                      <div className="relative w-full aspect-[3/2] bg-zinc-950 overflow-hidden">
                        <img src={svc.teaser_image_url} alt={svc.name} className="w-full h-full object-contain" />
                        <div className="absolute inset-x-0 bottom-0 h-8 bg-gradient-to-t from-black/50 to-transparent" />
                      </div>
                    )}
                    <div className="p-4 space-y-3">
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 flex-wrap">
                            <p className="font-body text-sm text-white font-semibold">{svc.name}</p>
                            <span className="text-[10px] bg-neon-cyan/10 text-neon-cyan border border-neon-cyan/25 px-2 py-0.5 rounded-full font-heading capitalize">
                              {subPreviews[svc.id]?.interval_label || "Suscripción periódica"}
                            </span>
                            <ServiceLikeButton
                              serviceId={svc.id}
                              ownerId={model?.user_id}
                              initialCount={svc.likes_count || 0}
                              initialLiked={!!svc.liked_by_me}
                            />
                          </div>
                          {svc.description && <p className="font-body text-xs text-zinc-400 mt-0.5">{svc.description}</p>}
                        </div>
                        <div className="text-right shrink-0">
                          <p className="text-[10px] text-zinc-500 font-body">{svc.price}€ / periodo</p>
                          {subPreviews[svc.id] && !subPreviews[svc.id].error && (
                            <p className="font-heading font-bold text-lg text-neon-cyan">
                              {subPreviews[svc.id].total_charged.toFixed(2)}€
                            </p>
                          )}
                        </div>
                      </div>
                      {/* Price breakdown */}
                      {subPreviews[svc.id] && !subPreviews[svc.id].error ? (
                        <div className="bg-zinc-900/60 rounded-xl p-3 space-y-1">
                          <div className="flex justify-between text-xs font-body text-zinc-400">
                            <span>Precio por periodo</span>
                            <span className="text-white font-semibold">{subPreviews[svc.id].total_price?.toFixed(2)}€</span>
                          </div>
                          <div className="flex justify-between text-xs font-body text-zinc-400">
                            <span>Cargo de servicio (+{feeInfo.pct}%)</span>
                            <span>+{((subPreviews[svc.id].total_charged || 0) - (subPreviews[svc.id].total_price || 0)).toFixed(2)}€</span>
                          </div>
                          <div className="flex justify-between text-xs font-body font-semibold text-white border-t border-zinc-700 pt-1 mt-1">
                            <span>Total a pagar</span>
                            <span className="text-neon-cyan">{subPreviews[svc.id].total_charged.toFixed(2)}€</span>
                          </div>
                        </div>
                      ) : subPreviews[svc.id]?.error ? (
                        <div className="text-xs text-red-400 font-body bg-red-950/30 rounded-lg px-3 py-2">
                          {subPreviews[svc.id].error}
                        </div>
                      ) : (
                        <div className="h-16 bg-zinc-900/40 rounded-xl animate-pulse" />
                      )}
                      <p className="text-[11px] text-zinc-500 font-body flex items-center gap-1">
                        <CreditCard className="w-3 h-3 shrink-0" />
                        Se renueva automáticamente cada {subPreviews[svc.id]?.interval_label || "periodo"}. Solo vía Stripe (tarjeta).
                      </p>
                      {subPreviews[svc.id]?.already_subscribed ? (
                        <div className="w-full text-center py-2.5 bg-zinc-800 rounded-xl text-zinc-400 text-sm font-heading font-bold">
                          Ya estás suscrito
                        </div>
                      ) : (
                        <Button
                          onClick={() => handleSubscribe(svc)}
                          disabled={subscribing === svc.id || !!(subPreviews[svc.id]?.error) || !subPreviews[svc.id]}
                          className="w-full bg-neon-cyan text-black font-heading font-bold rounded-xl py-2.5 disabled:opacity-50"
                          data-testid={`subscribe-btn-${svc.id}`}
                        >
                          {subscribing === svc.id ? (
                            <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Cargando…</>
                          ) : subPreviews[svc.id] && !subPreviews[svc.id].error ? (
                            `Suscribirme — ${subPreviews[svc.id].total_charged.toFixed(2)}€`
                          ) : (
                            "Calculando precio…"
                          )}
                        </Button>
                      )}
                    </div>
                  </div>
                ) : (
                  /* ── One-time service card with inline checkout ── */
                  <div key={svc.id}>
                    <div
                      role="button"
                      tabIndex={0}
                      onClick={() => handleSelectService(svc)}
                      onKeyDown={(e) => e.key === "Enter" && handleSelectService(svc)}
                      className={`w-full text-left transition-all duration-200 overflow-hidden cursor-pointer select-none border-2 ${
                        selectedService === svc.id
                          ? "rounded-t-xl border-neon-cyan bg-neon-cyan/10"
                          : "rounded-xl border-border hover:border-neon-cyan/50"
                      }`}
                      data-testid={`service-btn-${svc.id}`}
                    >
                      {svc.teaser_image_url && (
                        <div className="relative w-full aspect-[3/2] bg-zinc-950 overflow-hidden">
                          <img src={svc.teaser_image_url} alt={svc.name} className="w-full h-full object-contain" />
                          <div className="absolute inset-x-0 bottom-0 h-8 bg-gradient-to-t from-black/50 to-transparent" />
                        </div>
                      )}
                      <div className="flex items-center justify-between p-4">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-0.5 flex-wrap">
                            <p className="font-body text-sm text-white font-semibold">{svc.name}</p>
                            <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-primary/20 border border-primary/30 text-[10px] text-primary font-body shrink-0">
                              <MessageCircle className="w-2.5 h-2.5" />Chat
                            </span>
                            {!svc.is_subscription && (
                              svc.delivery_mode === "drive_integrated_viewer" ? (
                                <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-purple-500/15 border border-purple-500/25 text-[10px] text-purple-400 font-body shrink-0">
                                  <HardDrive className="w-2.5 h-2.5" />Visor D
                                </span>
                              ) : svc.premium_link ? (
                                <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-amber-500/15 border border-amber-500/25 text-[10px] text-amber-400 font-body shrink-0">
                                  <Link2 className="w-2.5 h-2.5" />Link
                                </span>
                              ) : (
                                <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-zinc-700/30 border border-zinc-600/30 text-[10px] text-zinc-500 font-body shrink-0">
                                  <Link2 className="w-2.5 h-2.5" />Sin link
                                </span>
                              )
                            )}
                            <ServiceLikeButton
                              serviceId={svc.id}
                              ownerId={model?.user_id}
                              initialCount={svc.likes_count || 0}
                              initialLiked={!!svc.liked_by_me}
                            />
                          </div>
                          {svc.description && <p className="font-body text-xs text-zinc-400">{svc.description}</p>}
                        </div>
                        <div className="flex items-center gap-2 shrink-0 ml-3">
                          <span className="font-heading font-bold text-lg text-neon-cyan">{svc.price}€</span>
                          <span className={`text-zinc-400 text-xs transition-transform duration-200 ${selectedService === svc.id ? "rotate-180" : ""}`}>▾</span>
                        </div>
                      </div>
                    </div>

                    {/* ── Inline payment panel ── */}
                    {selectedService === svc.id && (() => {
                      const baseAmt = svc.price;
                      const serviceFee = parseFloat((baseAmt * feeInfo.rate).toFixed(2));
                      const creatorReceives = parseFloat((baseAmt * 0.85).toFixed(2));
                      const totalToPay = parseFloat((baseAmt + serviceFee).toFixed(2));
                      return (
                        <motion.div
                          initial={{ opacity: 0, y: -4 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ duration: 0.18 }}
                          className="border-2 border-t-0 border-neon-cyan/40 rounded-b-xl bg-zinc-900/70 px-4 pb-4 pt-3 space-y-3"
                        >
                          {/* Chat notice */}
                          <div className="px-3 py-2 rounded-lg bg-primary/10 border border-primary/20 flex items-center gap-2" data-testid="service-chat-notice">
                            <MessageCircle className="w-3.5 h-3.5 text-primary shrink-0" />
                            {authUser?.user_id ? (
                              <p className="text-xs text-primary font-body">Al pagar se abrirá un chat privado con el creador.</p>
                            ) : (
                              <p className="text-xs text-zinc-400 font-body">
                                <Link to="/login" className="text-primary hover:underline font-bold">Inicia sesión</Link>
                                {" "}para habilitar el chat privado tras el pago.
                              </p>
                            )}
                          </div>
                          {/* Delivery mode notice */}
                          {svc.delivery_mode === "drive_integrated_viewer" ? (
                            <div className="px-3 py-2 rounded-lg bg-purple-500/10 border border-purple-500/20 flex items-center gap-2" data-testid="service-drive-notice">
                              <HardDrive className="w-3.5 h-3.5 text-purple-400 shrink-0" />
                              <p className="text-xs text-purple-300 font-body">Al completar el pago podrás ver el contenido a través del Visor Drive en el chat.</p>
                            </div>
                          ) : svc.premium_link ? (
                            <div className="px-3 py-2 rounded-lg bg-amber-500/10 border border-amber-500/20 flex items-center gap-2" data-testid="service-premium-link-notice">
                              <Link2 className="w-3.5 h-3.5 text-amber-400 shrink-0" />
                              <p className="text-xs text-amber-300 font-body">Al completar el pago recibirás automáticamente un enlace privado en el chat.</p>
                            </div>
                          ) : null}
                          {/* Fee breakdown */}
                          <div className="p-3 rounded-xl border border-border bg-card/40 space-y-1.5 text-sm font-body" data-testid="fee-breakdown">
                            <div className="flex justify-between items-center">
                              <span className="text-zinc-400">Propina al creador:</span>
                              <span className="text-neon-green font-bold">+{creatorReceives.toFixed(2)} €</span>
                            </div>
                            <div className="flex justify-between items-center">
                              <span className="text-zinc-400">Tarifa de servicio ({feeInfo.pct}%):</span>
                              <span className="text-zinc-400">{serviceFee.toFixed(2)} €</span>
                            </div>
                            {feeInfo.is_custom && (
                              <div className="flex items-center gap-1.5 text-xs text-amber-400 font-semibold">
                                <span>★</span>
                                <span>{feeInfo.label}{feeInfo.expires_at ? ` · hasta ${new Date(feeInfo.expires_at).toLocaleDateString("es-ES", { timeZone: "Europe/Madrid" })}` : ""}</span>
                              </div>
                            )}
                            <div className="flex justify-between items-center border-t border-border pt-1.5">
                              <span className="text-white font-semibold">Total a pagar:</span>
                              <span className="text-neon-pink font-bold text-base">{totalToPay.toFixed(2)} €</span>
                            </div>
                          </div>
                          {/* Payment method selector */}
                          <div className="grid grid-cols-2 gap-2">
                            <button
                              onClick={() => setPaymentMethod("stripe")}
                              className={`py-3 rounded-xl border-2 font-heading font-bold text-sm uppercase tracking-wide transition-all duration-200 flex items-center justify-center gap-2 ${
                                paymentMethod === "stripe" ? "border-neon-pink bg-neon-pink/10 text-neon-pink" : "border-border text-zinc-400 hover:border-zinc-500"
                              }`}
                              data-testid="method-stripe"
                            >
                              <CreditCard className="w-4 h-4" /> Tarjeta
                            </button>
                            <button
                              onClick={() => setPaymentMethod("paypal")}
                              className={`py-3 rounded-xl border-2 font-heading font-bold text-sm uppercase tracking-wide transition-all duration-200 ${
                                paymentMethod === "paypal" ? "border-[#0070ba] bg-[#0070ba]/10 text-[#0070ba]" : "border-border text-zinc-400 hover:border-zinc-500"
                              }`}
                              data-testid="method-paypal"
                            >
                              PayPal
                            </button>
                          </div>
                          {/* Pay */}
                          {paymentMethod === "stripe" ? (
                            <>
                              <Button
                                onClick={handlePay}
                                disabled={loading}
                                className="w-full h-14 text-base font-heading font-bold uppercase tracking-wide bg-neon-pink hover:bg-neon-pink/90 text-white border-2 border-transparent hover:border-white rounded-xl transition-all duration-200 disabled:opacity-40 shadow-[4px_4px_0px_0px_rgba(255,0,127,0.25)]"
                                data-testid="pay-button"
                              >
                                {loading ? (
                                  <span className="animate-pulse">Procesando...</span>
                                ) : (
                                  <span className="flex items-center justify-center gap-2">
                                    <CreditCard className="w-4 h-4" />
                                    Pagar {totalToPay.toFixed(2)} €
                                  </span>
                                )}
                              </Button>
                              <p className="text-center text-xs text-zinc-500 font-body">Pago seguro con Stripe.</p>
                            </>
                          ) : (
                            <div data-testid="paypal-buttons-container">
                              <PayPalScriptProvider options={{
                                clientId: paypalClientId,
                                currency: "EUR",
                                intent: "capture",
                                components: "buttons",
                                "enable-funding": "paypal",
                                "disable-funding": "paylater,venmo",
                              }}>
                                <PayPalButtons
                                  style={{ layout: "vertical", color: "black", shape: "rect", label: "pay", height: 50 }}
                                  forceReRender={[paypalClientId, svc.id]}
                                  createOrder={async () => {
                                    if (!requireAuth()) {
                                      // PayPal SDK needs us to throw or return a rejected promise to abort.
                                      throw new Error("login_required");
                                    }
                                    const body = buildTipBody();
                                    const res = await axios.post(`${API}/tips/paypal/create`, body, { withCredentials: true });
                                    return res.data.order_id;
                                  }}
                                  onApprove={async (data) => {
                                    try {
                                      await axios.post(`${API}/tips/paypal/capture/${data.orderID}`, {}, { withCredentials: true });
                                      navigate(`/success?session_id=${data.orderID}`);
                                    } catch (err) {
                                      const msg = err.response?.data?.detail || "Error al procesar el pago con PayPal";
                                      toast.error(msg);
                                    }
                                  }}
                                  onError={(err) => {
                                    console.error("PayPal error:", err);
                                    toast.error(
                                      paymentMode === "sandbox"
                                        ? "PayPal ha rechazado el pago. Asegúrate de usar una cuenta de comprador sandbox válida con saldo en EUR."
                                        : "PayPal ha rechazado el pago. Inténtalo de nuevo o contacta con el soporte."
                                    );
                                  }}
                                  onCancel={() => toast.info(t("tipPage.paymentCanceled"))}
                                />
                              </PayPalScriptProvider>
                              <p className="text-center text-xs text-zinc-500 mt-2 font-body">Pago seguro con PayPal.</p>
                            </div>
                          )}
                        </motion.div>
                      );
                    })()}
                  </div>
                )
              ))}
            </div>
          </motion.div>
          );
        })()}

        {/* ── Tip Amounts ─────────────────────────────────────────────── */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}>

          {/* Label */}
          <p style={{
            fontFamily: "'Barlow Condensed', sans-serif",
            fontWeight: 700,
            fontSize: '11px',
            letterSpacing: '0.12em',
            color: '#8a8a8a',
            textTransform: 'uppercase',
            marginBottom: '12px',
          }}>Propinas</p>

        {/* ── Barra de píldoras ──────────────────────────────────────── */}
          {(() => {
            // selectingId: rastrea qué píldora acaba de ser pulsada (para la animación)
            // Se gestiona con un ref para no provocar re-renders extra
            return null;
          })()}
          <style>{`
            @keyframes tipPulse {
              0%   { transform: scale(1);    box-shadow: 0 6px 18px rgba(255,0,102,0.4); }
              35%  { transform: scale(1.10); box-shadow: 0 12px 32px rgba(255,0,102,0.65); }
              65%  { transform: scale(1.02); box-shadow: 0 8px 24px rgba(255,0,102,0.5); }
              100% { transform: scale(1.03); box-shadow: 0 6px 18px rgba(255,0,102,0.4); }
            }
          `}</style>
          <div style={{
            background: '#111111',
            borderRadius: '100px',
            padding: '6px',
            border: '1px solid #222222',
            display: 'flex',
            gap: '6px',
          }}>
            {packages.map((pkg) => {
              const sel = selectedPackage === pkg.id;
              const animating = selectingId === pkg.id;
              return (
                <button
                  key={pkg.id}
                  onClick={() => {
                    handleSelectPackage(pkg);
                    setSelectingId(pkg.id);
                    setTimeout(() => setSelectingId(null), 350);
                  }}
                  data-testid={`tip-${pkg.id}`}
                  style={{
                    flex: 1,
                    height: '64px',
                    borderRadius: '100px',
                    border: 'none',
                    cursor: 'pointer',
                    fontFamily: "'Barlow Condensed', sans-serif",
                    fontWeight: 800,
                    fontSize: 'clamp(18px, 5vw, 26px)',
                    transition: animating ? 'none' : 'all 0.2s ease',
                    animation: animating ? 'tipPulse 0.32s ease-out forwards' : 'none',
                    background: sel ? '#FF0066' : 'transparent',
                    color: sel ? '#ffffff' : '#999999',
                    boxShadow: sel && !animating ? '0 6px 18px rgba(255,0,102,0.4)' : 'none',
                    transform: sel && !animating ? 'scale(1.03)' : 'scale(1)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {pkg.amount}€
                </button>
              );
            })}
          </div>

          {/* ── Otro monto ────────────────────────────────────────────── */}
          <button
            onClick={handleCustom}
            data-testid="tip-custom"
            style={{
              width: '100%',
              height: '64px',
              borderRadius: '100px',
              border: `1px dashed ${isCustom ? '#FF0066' : '#333333'}`,
              background: isCustom ? 'rgba(255,0,102,0.08)' : 'transparent',
              color: isCustom ? '#ff6699' : '#aaaaaa',
              fontSize: 'clamp(16px, 4vw, 22px)',
              fontWeight: 600,
              fontFamily: "'Barlow Condensed', sans-serif",
              cursor: 'pointer',
              marginTop: '16px',
              transition: 'all 0.2s ease',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            Otro monto
          </button>

          {/* Input monto personalizado */}
          {isCustom && (
            <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} className="mt-4">
              <div className="relative">
                <Input type="number" min={tipMin} step="0.01" placeholder={`Mínimo ${tipMin}€`} value={customAmount}
                  onChange={(e) => setCustomAmount(e.target.value)}
                  className="bg-transparent border-2 border-border rounded-xl h-14 px-4 text-xl text-white font-heading placeholder:text-zinc-600 focus:border-neon-green focus-visible:ring-neon-green"
                  data-testid="custom-amount-input" />
                <span className="absolute right-4 top-1/2 -translate-y-1/2 text-xl text-zinc-500 font-heading pointer-events-none">€</span>
              </div>
            </motion.div>
          )}
        </motion.div>

        {/* ── Separador ────────────────────────────────────────────────── */}
        <div style={{ height: '1px', background: '#1c1c1c', margin: '30px 0' }} />

        {/* ── Badge / desglose de comisión ─────────────────────────────── */}
        {!selectedService && (() => {
          let baseAmt = 0;
          if (selectedPackage) {
            const pkg = packages.find((p) => p.id === selectedPackage);
            baseAmt = pkg ? pkg.amount : 0;
          } else if (isCustom && customAmount && !isNaN(parseFloat(customAmount))) {
            baseAmt = parseFloat(customAmount);
          }

          // Sin monto seleccionado → no mostrar nada
          if (baseAmt <= 0) return null;

          // Con monto → desglose detallado (sin cambios visuales aquí)
          const serviceFee      = parseFloat((baseAmt * feeInfo.rate).toFixed(2));
          const creatorReceives = parseFloat((baseAmt * 0.85).toFixed(2));
          const totalToPay      = parseFloat((baseAmt + serviceFee).toFixed(2));
          return (
            <div className="p-3 rounded-xl border border-border bg-card/40 space-y-1.5 text-sm font-body" data-testid="fee-breakdown">
              <div className="flex justify-between items-center">
                <span className="text-zinc-400">Propina al creador:</span>
                <span className="text-neon-green font-bold">+{creatorReceives.toFixed(2)} €</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-zinc-400">Tarifa de servicio ({feeInfo.pct}%):</span>
                <span className="text-zinc-400">{serviceFee.toFixed(2)} €</span>
              </div>
              {feeInfo.is_custom && (
                <div className="flex items-center gap-1.5 text-xs text-amber-400 font-semibold">
                  <span>★</span>
                  <span>{feeInfo.label}{feeInfo.expires_at ? ` · hasta ${new Date(feeInfo.expires_at).toLocaleDateString("es-ES", { timeZone: "Europe/Madrid" })}` : ""}</span>
                </div>
              )}
              <div className="flex justify-between items-center border-t border-border pt-1.5">
                <span className="text-white font-semibold">Total a pagar:</span>
                <span className="text-neon-pink font-bold text-base">{totalToPay.toFixed(2)} €</span>
              </div>
            </div>
          );
        })()}

        {/* Message */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.12 }}>
          <p className="font-body text-sm text-zinc-400 mb-2 uppercase tracking-wide">
            Mensaje opcional
          </p>
          <MessageInputWithEmojis
            value={message}
            onChange={setMessage}
            maxLength={MAX_MSG}
            testId="tip-message-input"
          />
        </motion.div>
        {/* Payment method + Pay — only for packages / custom amounts */}
        {!selectedService && (
          <>
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 }}>
              <p className="font-body text-sm text-zinc-400 mb-3 uppercase tracking-wide">Metodo de pago</p>
              <div className="grid grid-cols-2 gap-3">
                <button onClick={() => setPaymentMethod("stripe")}
                  className={`py-4 rounded-xl border-2 font-heading font-bold text-sm uppercase tracking-wide transition-all duration-200 flex items-center justify-center gap-2 ${
                    paymentMethod === "stripe" ? "border-neon-pink bg-neon-pink/10 text-neon-pink" : "border-border text-zinc-400 hover:border-zinc-500"
                  }`} data-testid="method-stripe">
                  <CreditCard className="w-4 h-4" /> Tarjeta
                </button>
                <button onClick={() => setPaymentMethod("paypal")}
                  className={`py-4 rounded-xl border-2 font-heading font-bold text-sm uppercase tracking-wide transition-all duration-200 ${
                    paymentMethod === "paypal" ? "border-[#0070ba] bg-[#0070ba]/10 text-[#0070ba]" : "border-border text-zinc-400 hover:border-zinc-500"
                  }`} data-testid="method-paypal">
                  PayPal
                </button>
              </div>
            </motion.div>

            {/* Pay */}
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}>
              {paymentMethod === "stripe" ? (
                <>
                  <Button onClick={handlePay} disabled={loading || !hasSelection}
                    className="w-full h-16 text-lg font-heading font-bold uppercase tracking-wide bg-neon-pink hover:bg-neon-pink/90 text-white border-2 border-transparent hover:border-white rounded-xl transition-all duration-200 disabled:opacity-40 disabled:cursor-not-allowed shadow-[4px_4px_0px_0px_rgba(255,0,127,0.25)]"
                    data-testid="pay-button">
                    {loading ? <span className="animate-pulse">Procesando...</span> : (
                      <span className="flex items-center justify-center gap-2">
                        <CreditCard className="w-5 h-5" />
                        {getTotalToPay() ? `${t("tipPage.payPrefix")} ${getTotalToPay()?.toFixed(2)} €` : t("tipPage.selectAmountFirst")}
                      </span>
                    )}
                  </Button>
                  <p className="text-center text-xs text-zinc-500 mt-3 font-body">Pago seguro con Stripe.</p>
                </>
              ) : (
                <div data-testid="paypal-buttons-container">
                  {hasSelection ? (
                    <PayPalScriptProvider options={{
                      clientId: paypalClientId,
                      currency: "EUR",
                      intent: "capture",
                      components: "buttons",
                      "enable-funding": "paypal",
                      "disable-funding": "paylater,venmo",
                    }}>
                      <PayPalButtons
                        style={{ layout: "vertical", color: "black", shape: "rect", label: "pay", height: 55 }}
                        forceReRender={[paypalClientId, selectedPackage, customAmount]}
                        createOrder={async () => {
                          if (!requireAuth()) {
                            throw new Error("login_required");
                          }
                          const body = buildTipBody();
                          const res = await axios.post(`${API}/tips/paypal/create`, body, { withCredentials: true });
                          return res.data.order_id;
                        }}
                        onApprove={async (data) => {
                          try {
                            await axios.post(`${API}/tips/paypal/capture/${data.orderID}`, {}, { withCredentials: true });
                            navigate(`/success?session_id=${data.orderID}`);
                          } catch (err) {
                            const msg = err.response?.data?.detail || "Error al procesar el pago con PayPal";
                            toast.error(msg);
                          }
                        }}
                        onError={(err) => {
                          console.error("PayPal error:", err);
                          toast.error(
                            paymentMode === "sandbox"
                              ? "PayPal ha rechazado el pago. Asegúrate de usar una cuenta de comprador sandbox válida con saldo en EUR."
                              : "PayPal ha rechazado el pago. Inténtalo de nuevo o contacta con el soporte."
                          );
                        }}
                        onCancel={() => toast.info(t("tipPage.paymentCanceled"))}
                      />
                      <p className="text-center text-xs text-zinc-500 mt-3 font-body">Pago seguro con PayPal.</p>
                    </PayPalScriptProvider>
                  ) : (
                    <div className="py-8 text-center">
                      <p className="text-zinc-500 font-body">Selecciona un monto para ver PayPal</p>
                    </div>
                  )}
                </div>
              )}
            </motion.div>
          </>
        )}
      </div>


      <LoginRequiredModal
        open={needLogin}
        onClose={() => setNeedLogin(false)}
        returnPath={typeof window !== "undefined" ? window.location.pathname + window.location.search : "/"}
      />

      {/* ── Modal confirmación solicitud chat privado ── */}
      {showChatConfirm && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          style={{ background: "rgba(0,0,0,0.75)", backdropFilter: "blur(4px)" }}
          onClick={() => setShowChatConfirm(false)}
          data-testid="chat-confirm-overlay"
        >
          <div
            className="relative w-full max-w-sm rounded-2xl border border-zinc-700 bg-zinc-900 p-6 shadow-2xl"
            onClick={e => e.stopPropagation()}
            data-testid="chat-confirm-modal"
          >
            <button
              onClick={() => setShowChatConfirm(false)}
              className="absolute top-3 right-3 text-zinc-500 hover:text-white transition-colors"
              data-testid="chat-confirm-close"
            >
              <X className="w-4 h-4" />
            </button>

            <div className="flex flex-col items-center text-center gap-4">
              <div className="w-12 h-12 rounded-full bg-neon-cyan/10 border border-neon-cyan/30 flex items-center justify-center">
                <MessageCircle className="w-6 h-6 text-neon-cyan" />
              </div>

              <div>
                <p className="text-white font-heading font-bold text-base">
                  ¿Solicitar chat privado?
                </p>
                <p className="text-zinc-400 text-sm mt-1 font-body">
                  Se enviará una solicitud a <span className="text-white font-semibold">{model?.name}</span>. Ella deberá aceptarla para poder chatear.
                </p>
              </div>

              <div className="flex gap-3 w-full">
                <button
                  onClick={() => setShowChatConfirm(false)}
                  className="flex-1 py-2.5 rounded-xl border border-zinc-700 text-zinc-400 hover:text-white hover:border-zinc-500 text-sm font-heading font-bold uppercase tracking-wide transition-all"
                  data-testid="chat-confirm-cancel"
                >
                  Cancelar
                </button>
                <button
                  onClick={() => { setShowChatConfirm(false); handleRequestPrivateChat(); }}
                  disabled={requestingChat}
                  className="flex-1 py-2.5 rounded-xl bg-neon-cyan/10 border border-neon-cyan/40 hover:bg-neon-cyan/20 text-neon-cyan text-sm font-heading font-bold uppercase tracking-wide transition-all disabled:opacity-50"
                  data-testid="chat-confirm-send"
                >
                  {requestingChat ? <RefreshCw className="w-4 h-4 animate-spin mx-auto" /> : "Confirmar"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
